import { z } from 'zod/v4';

const LIST_RISK_AUDIT_TYPES_DEFAULT_PAGE_SIZE = 20;
const LIST_RISK_AUDIT_TYPES_MAX_PAGE_SIZE = 500;

/**
 * `GET /risk-analysis/audit-types` querystring, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/audit-type.js`'s
 * `list.querystring`.
 *
 * `sort` is dropped: the legacy `sortingColumnsMapping` for this endpoint is
 * `{}` (empty), so a `sort` value was always accepted and always ignored —
 * results are hard-ordered by `name ASC` regardless. This is a narrower,
 * risk-rule-builder-specific view of the same `audits_service_audit_types`
 * catalogue `GET /api/audit-types` (`modules/audits`) already exposes in
 * full — different guard (`risk:read` vs `audit:read`), narrower response
 * shape, stricter filter validation (unmapped filter keys 400 instead of
 * being silently ignored), and the `withFindings` toggle neither the general
 * endpoint nor its filter builder has a concept of.
 */
export const GetRiskAuditTypesQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(LIST_RISK_AUDIT_TYPES_MAX_PAGE_SIZE)
    .default(LIST_RISK_AUDIT_TYPES_DEFAULT_PAGE_SIZE),
  filter: z.string().optional(),
  // Not `z.coerce.boolean()`: that coerces ANY non-empty string — including
  // the literal `"false"` — to `true`, since `Boolean('false')` is `true` in
  // JS. `z.stringbool()` actually parses `"true"`/`"false"` (plus a few
  // aliases) and rejects anything else, matching the legacy AJV
  // `{type: 'boolean'}` validation this ports.
  withFindings: z.stringbool().optional(),
});

export type GetRiskAuditTypesQuery = z.infer<
  typeof GetRiskAuditTypesQuerySchema
>;

/**
 * One row of `GET /risk-analysis/audit-types`, ported from the legacy
 * `auditType` response definition
 * (`2025-06/risk-analysis-service/routes/schemas/v3/responses.js`).
 *
 * `name`/`category`/`isDeprecated`/`impactAreaId` are all nullable —
 * `audits_service_audit_types`'s columns are nullable (`impactAreaId` is
 * null on every row in current data), even though the legacy JSON-schema
 * declared them non-nullable. `isDeprecated` stays a raw `0 | 1 | null`
 * number, matching the legacy wire shape exactly (not coerced to boolean,
 * unlike `modules/audits`' `AuditTypeRowSchema`).
 */
export const RiskAuditTypeRowSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  category: z.string().nullable(),
  isDeprecated: z.number().nullable(),
  impactAreaId: z.string().nullable(),
});

export type RiskAuditTypeRow = z.infer<typeof RiskAuditTypeRowSchema>;
